import { useId, useState } from 'react';
import { exchangeRateRefusal } from '@/domain';
import { useFinanceSettings, useSetExchangeRate } from '@/application/usecases/useFinance';
import { useSession } from '@/application/usecases/useSession';
import { failureWords } from '@/shared/failure/failure';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { useMoneyChoice } from '@/ui/money/moneyView';
import { Button } from '@/ui/primitives/Button';
import { Modal } from '@/ui/primitives/Modal';
import { toast } from '@/ui/primitives/toastStore';
import styles from './CurrencyBar.module.css';

const t = strings.currency;

/**
 * The system's money and the dollar rate, over Payments. The lab's request of
 * 17 October 2026, item 13: "currency of the system make it in AED, and allow
 * exchange manually to dollars, exchange rate will be entered manually by
 * accountant."
 *
 * Amounts are in dirhams. A reader may show every amount on this device in
 * dollars instead, at the rate Accounts set, which the bar says beside the
 * choice with who set it and when; a holder of pay sets it here. The rate only
 * ever changes how an amount is shown: everything is kept and typed in
 * dirhams.
 */
export function CurrencyBar() {
  const choice = useMoneyChoice();
  const settings = useFinanceSettings();
  const pays = useSession().data?.permissions.includes('pay') === true;
  const [editing, setEditing] = useState(false);

  const rate = settings.data?.usdRate ?? null;
  const showsDollars = choice.inDollars && rate !== null;

  return (
    <div className={styles.bar} role="group" aria-label={t.label}>
      <span className={styles.lbl}>{t.label}</span>
      <div className={styles.choices}>
        <button type="button" aria-pressed={!showsDollars} onClick={() => choice.choose?.(false)}>
          {t.aed}
        </button>
        <button
          type="button"
          aria-pressed={showsDollars}
          disabled={rate === null || choice.choose === undefined}
          onClick={() => choice.choose?.(true)}
        >
          {t.usd}
        </button>
      </div>
      <span className={styles.rate}>
        {settings.data === undefined
          ? ''
          : rate === null
            ? t.noRate
            : `${t.rate(String(rate))}${
                settings.data.usdRateSetAt === null
                  ? ''
                  : ` · ${t.setBy(settings.data.usdRateSetByName, formatDay(settings.data.usdRateSetAt))}`
              }`}
      </span>
      {pays && (
        <Button variant="text" onClick={() => setEditing(true)}>
          {rate === null ? t.setRate : t.changeRate}
        </Button>
      )}
      {editing && <RateDialog current={rate} onClose={() => setEditing(false)} />}
    </div>
  );
}

/** The rate, typed by hand. The dialog is the question: Save is the answer. */
function RateDialog({ current, onClose }: { current: number | null; onClose: () => void }) {
  const save = useSetExchangeRate();
  const inputId = useId();
  const [typed, setTyped] = useState(current === null ? '' : String(current));
  const [problem, setProblem] = useState<string | null>(null);

  function submit() {
    const rate = typed.trim() === '' ? null : Number(typed);
    const refusal = exchangeRateRefusal(rate === null || Number.isNaN(rate) ? null : rate);
    if (refusal !== null || rate === null) {
      setProblem(refusal ?? t.badRate);
      return;
    }
    save.mutate(rate, {
      onSuccess: () => {
        toast(t.saved(String(rate)), { tone: 'success' });
        onClose();
      },
      onError: (error: Error) => setProblem(failureWords(error)),
    });
  }

  return (
    <Modal
      title={t.rateTitle}
      lead={t.rateLead}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={save.isPending}>
            {t.cancel}
          </Button>
          <Button variant="primary" onClick={submit} loading={save.isPending} disabled={save.isPending}>
            {t.saveRate}
          </Button>
        </>
      }
    >
      <label className={styles.fieldLabel} htmlFor={inputId}>
        {t.rateLabel}
      </label>
      <input
        id={inputId}
        className={styles.input}
        type="number"
        inputMode="decimal"
        min={0}
        step="0.0001"
        value={typed}
        aria-invalid={problem !== null}
        onChange={(event) => {
          setTyped(event.target.value);
          setProblem(null);
        }}
      />
      {problem !== null && (
        <p className={styles.problem} role="alert">
          {problem}
        </p>
      )}
    </Modal>
  );
}
